import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

/**
 * App-wide error boundary so a render error in any page shows a recoverable
 * message instead of a blank white screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface for debugging without crashing the app.
    // eslint-disable-next-line no-console
    console.error("UI ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center" data-testid="error-boundary">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-danger-light text-danger">
            <AlertTriangle size={32} />
          </div>
          <h2 className="font-heading text-xl font-bold text-ink">Something went wrong on this page</h2>
          <p className="mt-2 max-w-md text-sm text-ink-soft">
            Don&apos;t worry — your data is safe. Try reloading this page. If it keeps happening, please let us know.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => { this.reset(); window.location.assign("/"); }}
              className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink-soft transition-colors hover:bg-stone-50"
              data-testid="error-go-home"
            >
              Go to Home
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
              data-testid="error-reload"
            >
              <RotateCw size={16} /> Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
